import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './SessionLog.css';
import DownloadFileModal from '../components/DownloadFileModal';
import ConnectionsRedirectModal from '../components/ConnectionsRedirectModal';
import { api } from '../config/api';

interface ParkingSession {
    sessionId: string;
    plateNumber: string;
    lotId: string;
    entryTime: string;
    exitTime: string;
    departed: boolean;
    forceEnd: boolean;
    totalAmount: number;
    ownerName?: string;
    entryAlprId?: string;
    exitAlprId?: string;
    advancedPricingEnabled?: boolean;
    pricingId?: string;
    vehicleRegistered?: boolean;
    sessionType?: "Permitted" | string;
    userType?: "New" | string;
    vehicleType?: "ICE" | string;
    dwellTime?: number;
    dwellPeriodTimeScale?: string;
    paymentStatus?: string;
}

interface SortConfig {
    key: keyof ParkingSession | 'duration' | 'status' | 'paymentStatus';
    direction: 'ascending' | 'descending';
}

interface CursorPageResponse<T> {
    content: T[];
    nextCursorId: string | null;
    nextCursorTimestamp: string | null;
    hasMore: boolean;
    totalElements: number;
}

interface GenericResponse<T> {
    success: boolean;
    data: T;
    message: string;
    status: number;
}

const DEFAULT_PAGE_SIZE = 40; // Default page size for sessions data

const SessionLog: React.FC = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const [sessions, setSessions] = useState<ParkingSession[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: 'entryTime',
        direction: 'descending',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState<ParkingSession | null>(null);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showConnectionsModal, setShowConnectionsModal] = useState(false);
    const [lotDisplayId, setLotDisplayId] = useState<string>('');
    const [isTableNarrow, setIsTableNarrow] = useState(false);
    const tableRef = useRef<HTMLTableElement>(null);

    // Add state for selected entries
    const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
    const [isForceEnding, setIsForceEnding] = useState(false);

    // Pagination state
    const [nextCursorId, setNextCursorId] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const loaderRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Add a ref for debounce timer
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Add state for showing more details in modal
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);

    // Add state for payment status edit functionality
    const [showPaymentStatusEdit, setShowPaymentStatusEdit] = useState(false);
    const [isUpdatingPaymentStatus, setIsUpdatingPaymentStatus] = useState(false);

    useEffect(() => {
        const checkTableWidth = () => {
            if (tableRef.current) {
                setIsTableNarrow(tableRef.current.offsetWidth < 600);
            }
        };

        // Initial check
        checkTableWidth();

        // Add resize listener
        window.addEventListener('resize', checkTableWidth);

        // Cleanup
        return () => window.removeEventListener('resize', checkTableWidth);
    }, []);

    // Load more data when scrolling to the bottom - with debounce
    const loadMoreData = useCallback(() => {
        if (!isLoading && hasMore && nextCursorId) {
            // Debouncing mechanism to prevent rapid successive calls
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                fetchSessions(nextCursorId, false);
                debounceTimerRef.current = null;
            }, 300); // 300ms debounce
        }
    }, [isLoading, hasMore, nextCursorId]);

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        // Only set up the observer if there's more data to fetch
        if (!hasMore || isLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && hasMore && !isLoading) {
                    loadMoreData();
                }
            },
            {
                threshold: 0.5,  // More conservative - when 50% of element is visible
                rootMargin: '100px' // Only trigger when within 100px of viewport
            }
        );

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) {
                observer.unobserve(loaderRef.current);
            }
            observer.disconnect();
        };
    }, [hasMore, isLoading, loadMoreData]);

    useEffect(() => {
        if (lotId) {
            fetchSessions();
            fetchLotDisplayId();
        }
    }, [lotId]);

    const fetchSessions = async (cursor?: string | null, reset: boolean = true) => {
        try {
            setIsLoading(true);

            // Construct URL with cursor if provided
            let url = `/parkingsessions/get-sessions-by-lot-id/${lotId}?limit=${DEFAULT_PAGE_SIZE}`;
            if (cursor) {
                url += `&cursor=${cursor}`;
            }

            const response = await api.get(url);

            const responseData: GenericResponse<CursorPageResponse<ParkingSession>> = response.data;
            const sessionsData = responseData.data.content;

            // Update pagination state
            setNextCursorId(responseData.data.nextCursorId);

            // Set hasMore based on API response and whether we got any data
            const newHasMore = responseData.data.hasMore && sessionsData.length > 0;
            setHasMore(newHasMore);

            // If no more data or empty response, prevent further loading
            if (!newHasMore || sessionsData.length === 0) {
                console.log("No more data available or empty response");
            }

            // Sort by entry time (newest first)
            const sortedEntries = sessionsData.sort((a, b) =>
                new Date(b.entryTime).getTime() - new Date(a.entryTime).getTime()
            );

            // Either replace existing data or append to it
            if (reset) {
                setSessions(sortedEntries);
                // Don't clear selected entries when data is reset
                // This preserves selections during search/sort operations
            } else {
                // Add a check for duplicates before appending
                const existingIds = new Set(sessions.map(entry => entry.sessionId));
                const newEntries = sortedEntries.filter(entry => !existingIds.has(entry.sessionId));

                if (newEntries.length > 0) {
                    setSessions(prevEntries => [...prevEntries, ...newEntries]);
                } else {
                    // If we got no new entries, even if hasMore is true, stop pagination
                    setHasMore(false);
                    console.log("No new unique entries found, stopping pagination");
                }
            }
        } catch (error) {
            console.error('Error fetching sessions data:', error);
            if (reset) {
                setSessions([]);
            }
            // In case of error, stop pagination to prevent infinite retries
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch lot display ID when component loads
    const fetchLotDisplayId = async () => {
        try {
            const response = await api.get(`/parkinglots/get-by-id/${lotId}`);
            const lotData = response.data;
            if (lotData.success && lotData.data.displayLotId) {
                setLotDisplayId(lotData.data.displayLotId);
            }
        } catch (err) {
            console.error('Error fetching lot details:', err);
        }
    };

    // Format display lot ID for filename (extract last 8 characters and format as xxxx-xxxx)
    const formatDisplayLotId = (id: string): string => {
        if (!id || id.length < 8) return id;
        const last8 = id.slice(-8);
        return `${last8.slice(0, 4)}-${last8.slice(4)}`;
    };

    // Generate default filename based on the current date and the lot ID
    const getDefaultFilename = (): string => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        const formattedLotId = formatDisplayLotId(lotDisplayId);
        return `${formattedLotId}_SessionLog_${formattedDate}.csv`;
    };

    // Open the download modal with a 10% chance of showing connections modal instead
    const handleOpenDownloadModal = () => {
        // Random number between 0 and 1
        const random = Math.random();

        // 20% chance to show the connections modal
        if (random < 0.2) {
            setShowConnectionsModal(true);
        } else {
            setShowDownloadModal(true);
        }
    };

    // Handle closing the connections modal
    const handleCloseConnectionsModal = () => {
        setShowConnectionsModal(false);
    };

    // Create a helper function to ensure unique keys
    const getUniqueKey = (session: ParkingSession): string => {
        // Use a combination of plate number, entry time, and lot ID for more stable keys
        // This ensures the key remains consistent even if sessionId changes
        return `${session.plateNumber}-${session.entryTime}-${session.lotId}`;
    };

    // Handle checkbox selection
    const handleCheckboxChange = (session: ParkingSession) => {
        const key = getUniqueKey(session);
        setSelectedEntries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    // Handle select all checkbox
    const handleSelectAll = () => {
        if (selectedEntries.size === filteredResults.length) {
            // If all are selected, deselect all
            setSelectedEntries(new Set());
        } else {
            // Select all
            const allKeys = filteredResults.map((session) => getUniqueKey(session));
            setSelectedEntries(new Set(allKeys));
        }
    };

    // Get selected entries for download
    const getSelectedEntriesForDownload = (): ParkingSession[] => {
        if (selectedEntries.size === 0) {
            return filteredResults; // Return all if none selected
        }

        return filteredResults.filter((session) => {
            const key = getUniqueKey(session);
            return selectedEntries.has(key);
        });
    };

    // CSV Download
    const handleDownload = (filename: string) => {
        const header = 'SessionID,PlateNumber,LotID,EntryTime,ExitTime,Departed,ForceEnd,TotalAmount,OwnerName,EntryAlprID,ExitAlprID,AdvancedPricingEnabled,PricingID,VehicleRegistered,SessionType,UserType,VehicleType,DwellTime,DwellPeriodTimeScale,Duration,Status,PaymentStatus\n';
        const sessionsToDownload = getSelectedEntriesForDownload();
        const rows = sessionsToDownload.map((session) => {
            const duration = calculateDuration(session.entryTime, session.exitTime);
            const status = getSessionStatus(session);
            const paymentStatus = getPaymentStatusDisplayText(session.paymentStatus);
            return `${session.sessionId},${session.plateNumber},${session.lotId},${session.entryTime},${session.exitTime || ''},${session.departed},${session.forceEnd},${session.totalAmount !== null ? session.totalAmount : ''},${session.ownerName || ''},${session.entryAlprId || ''},${session.exitAlprId || ''},${session.advancedPricingEnabled || ''},${session.pricingId || ''},${session.vehicleRegistered || ''},${session.sessionType || ''},${session.userType || ''},${session.vehicleType || ''},${session.dwellTime || ''},${session.dwellPeriodTimeScale || ''},${duration},${status},${paymentStatus}`;
        });
        const blob = new Blob([header + rows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
        setShowDownloadModal(false);
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase();

        // When search query changes, reset and fetch from beginning
        if (query !== searchQuery && query === '') {
            setNextCursorId(null);
            fetchSessions();
        }

        // Don't clear selected entries - they will be preserved
        // The selected entries are stored by sessionId which should be stable

        setSearchQuery(query);
    };

    const calculateDuration = (entryTime: string, exitTime: string | null): string => {
        const start = new Date(entryTime);
        const end = exitTime ? new Date(exitTime) : new Date();
        const durationMs = end.getTime() - start.getTime();

        const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

        if (isTableNarrow) {
            // Mobile format
            if (days > 0) {
                return `${days}d ${hours}h`;
            } else if (hours > 0) {
                return `${hours}h ${minutes}m`;
            } else {
                return `${minutes}m`;
            }
        } else {
            // Desktop format
            if (days > 0) {
                return `${days} days ${hours} hrs`;
            } else if (hours > 0) {
                return `${hours} hrs ${minutes} mins`;
            } else {
                return `${minutes} mins`;
            }
        }
    };

    const getSessionStatus = (session: ParkingSession): string => {
        if (session.forceEnd) return 'Force End';
        return session.departed ? 'Departed' : 'Active';
    };

    const getPaymentStatusColor = (paymentStatus?: string): string => {
        if (!paymentStatus) return 'gray';

        // Green statuses
        if (['NOT_REQUIRED', 'PAID_SINGLE', 'PAID_WALLET', 'PAID_AUTOFILL'].includes(paymentStatus)) {
            return 'green';
        }

        // Yellow statuses
        if (['PENDING', 'INPROGRESS', 'SESSION_STARTED'].includes(paymentStatus)) {
            return 'yellow';
        }

        // Red statuses
        if (['UNPAID', 'FAILED', 'EXPIRED', 'CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'DISPUTED', 'CHARGEBACK', 'REQUIRES_MANUAL_ACTION', 'AUTO_REFUND_INITIATED'].includes(paymentStatus)) {
            return 'red';
        }

        return 'gray';
    };

    const getPaymentStatusDisplayText = (paymentStatus?: string): string => {
        if (!paymentStatus) return 'Unknown';

        // Convert enum values to display-friendly text
        const statusMap: { [key: string]: string } = {
            'NOT_REQUIRED': 'No Charge',
            'PENDING': 'Pending',
            'INPROGRESS': 'Processing',
            'SESSION_STARTED': 'Active Session',
            'UNPAID': 'Unpaid',
            'PAID_SINGLE': 'Paid',
            'PAID_WALLET': 'Paid',
            'PAID_AUTOFILL': 'Paid',
            'FAILED': 'Failed',
            'EXPIRED': 'Expired',
            'CANCELLED': 'Cancelled',
            'REFUNDED': 'Refunded',
            'PARTIALLY_REFUNDED': 'Refunded*',
            'DISPUTED': 'Disputed',
            'CHARGEBACK': 'Chargeback',
            'REQUIRES_MANUAL_ACTION': 'Please Fix',
            'AUTO_REFUND_INITIATED': 'Refunding'
        };

        return statusMap[paymentStatus] || paymentStatus;
    };

    const handleRowClick = (session: ParkingSession) => {
        setSelectedSession(session);
        setShowDetailModal(true);
    };

    const handleDetailModalClose = () => {
        setShowDetailModal(false);
        setSelectedSession(null);
    };

    const handleForceEndSession = async () => {
        if (!selectedSession || !lotId) return;

        try {
            setIsForceEnding(true);
            const currentTime = new Date().toISOString();

            const response = await api.put('/parkingsessions/force-end-session', null, {
                params: {
                    lotId: lotId,
                    sessionId: selectedSession.sessionId,
                    forceEndedBy: 'admin', // You might want to get this from user context
                    exitTime: currentTime
                }
            });

            if (response.data.success) {
                // Reset pagination state and fetch data from the beginning
                setNextCursorId(null);
                setHasMore(false);
                fetchSessions();
                setShowDetailModal(false);
                setSelectedSession(null);
            } else {
                console.error('Failed to force end session:', response.data.message);
            }
        } catch (error) {
            console.error('Error force ending session:', error);
        } finally {
            setIsForceEnding(false);
        }
    };

    const handlePaymentStatusUpdate = async (newPaymentStatus: string) => {
        if (!selectedSession) return;

        try {
            setIsUpdatingPaymentStatus(true);

            const response = await api.put('/parkingsessions/override-payment-status', {
                sessionId: selectedSession.sessionId,
                paymentStatus: newPaymentStatus,
                modifiedBy: 'admin' // You might want to get this from user context
            });

            if (response.data.success) {
                // Update the selected session with new data
                setSelectedSession(response.data.data);

                // Update the session in the main list
                setSessions(prevSessions =>
                    prevSessions.map(session =>
                        session.sessionId === selectedSession.sessionId
                            ? response.data.data
                            : session
                    )
                );

                setShowPaymentStatusEdit(false);
            } else {
                console.error('Failed to update payment status:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating payment status:', error);
        } finally {
            setIsUpdatingPaymentStatus(false);
        }
    };

    // Handle download of session data as CSV
    const handleDownloadSessionData = (session: ParkingSession) => {
        const headers = Object.keys(session);
        const values = Object.values(session);

        // Construct the desired filename: Plate_Status_Duration_Session.csv
        const plateNumber = session.plateNumber || 'UnknownPlate';
        const status = getSessionStatus(session).replace(/\s+/g, '_'); // Replace spaces with underscores
        const duration = calculateDuration(session.entryTime, session.exitTime).replace(/\s+/g, '_'); // Replace spaces with underscores
        const filename = `${plateNumber}_${status}_${duration}_Session.csv`;

        // Format date objects to locale string for readability
        const formattedValues = values.map((value, index) => {
            if (headers[index].includes('Time') && typeof value === 'string') {
                try {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleString();
                    }
                } catch (e) {
                    console.error('Error parsing date for CSV:', value, e);
                }
            }
            // Handle boolean values
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }
            // Handle null/undefined
            if (value === null || value === undefined) {
                return '';
            }
            // Ensure commas and quotes within fields are handled correctly (basic CSV escaping)
            let stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                stringValue = '"' + stringValue.replace(/"/g, '""') + '"';
            }
            return stringValue;
        });

        const csvContent = `${headers.join(',')}\n${formattedValues.join(',')}`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        if (showDetailModal) {
            const handleEsc = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    handleDetailModalClose();
                }
            };
            window.addEventListener('keydown', handleEsc);
            return () => window.removeEventListener('keydown', handleEsc);
        }
    }, [showDetailModal]);

    // Close payment status edit dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (showPaymentStatusEdit && !target.closest('.payment-status-edit-dropdown') && !target.closest('.edit-payment-status-button')) {
                setShowPaymentStatusEdit(false);
            }
        };

        if (showPaymentStatusEdit) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPaymentStatusEdit]);

    const filteredResults = sessions
        .filter((session) => {
            const normalizedData = `${session.plateNumber} ${session.entryTime} ${session.exitTime} ${getSessionStatus(session)} ${getPaymentStatusDisplayText(session.paymentStatus)}`.toLowerCase();
            return normalizedData.includes(searchQuery);
        })
        .sort((a, b) => {
            if (sortConfig.key) {
                let aVal: string | number = '';
                let bVal: string | number = '';

                if (sortConfig.key === 'duration') {
                    aVal = new Date(a.exitTime || new Date()).getTime() - new Date(a.entryTime).getTime();
                    bVal = new Date(b.exitTime || new Date()).getTime() - new Date(b.entryTime).getTime();
                } else if (sortConfig.key === 'status') {
                    aVal = getSessionStatus(a);
                    bVal = getSessionStatus(b);
                } else if (sortConfig.key === 'paymentStatus') {
                    aVal = getPaymentStatusDisplayText(a.paymentStatus);
                    bVal = getPaymentStatusDisplayText(b.paymentStatus);
                } else if (sortConfig.key === 'totalAmount') {
                    aVal = a.totalAmount;
                    bVal = b.totalAmount;
                } else if (sortConfig.key === 'entryTime') {
                    // Use exitTime if available, otherwise use entryTime
                    // This ensures completed sessions (with exit times) appear at top when sorted descending
                    aVal = a.exitTime || a.entryTime;
                    bVal = b.exitTime || b.entryTime;
                } else {
                    if (typeof a[sortConfig.key] === 'boolean') {
                        aVal = a[sortConfig.key] ? 1 : 0;
                        bVal = b[sortConfig.key] ? 1 : 0;
                    } else {
                        aVal = a[sortConfig.key] as string;
                        bVal = b[sortConfig.key] as string;
                    }
                }

                if (sortConfig.direction === 'ascending') {
                    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                } else {
                    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
                }
            }
            return 0;
        });

    const handleSort = (key: keyof ParkingSession | 'duration' | 'status') => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className={`session-log ${sessions.length > 0 ? 'has-data' : ''}`}>
            <div className="title-section">
                <h1>Session Log</h1>
                <button
                    className="info-button"
                    onClick={() => setShowInfoModal(true)}
                    title="Learn about session statuses and payment statuses"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                </button>
            </div>
            <div className="search-and-download">
                <div className="search-bar">
                    <img src="/assets/SearchBarIcon.svg" alt="Search" />
                    <input
                        type="text"
                        placeholder="Search Plate, Date, Duration, Amount, Status or Payment"
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                </div>

                <button className="download-button" onClick={handleOpenDownloadModal}>
                    <span className="button-text">
                        <span className="full-text">
                            {selectedEntries.size === 0 || selectedEntries.size === filteredResults.length
                                ? 'Download All'
                                : `Download (${selectedEntries.size})`}
                        </span>
                        <span className="short-text">
                            {selectedEntries.size === 0 || selectedEntries.size === filteredResults.length
                                ? 'Download All'
                                : `Download (${selectedEntries.size})`}
                        </span>
                    </span>
                </button>
            </div>
            <table className={`session-log-table ${sessions.length > 0 ? 'has-data' : ''}`} ref={tableRef}>
                <thead>
                    <tr>
                        <th style={{ width: '30px', textAlign: 'center' }}>
                            <div className="custom-checkbox" style={{ cursor: 'pointer', margin: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={selectedEntries.size === filteredResults.length && filteredResults.length > 0}
                                    onChange={handleSelectAll}
                                    className="hidden-checkbox"
                                    style={{ position: 'absolute', cursor: 'pointer' }}
                                />
                                <span
                                    className={`checkbox-styler ${selectedEntries.size === filteredResults.length && filteredResults.length > 0 ? 'checked' : ''}`}
                                    onClick={handleSelectAll}
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            </div>
                        </th>
                        <th onClick={() => handleSort('plateNumber')} className="sortable-column">
                            <div className="transaction-log-header-content">
                                <span className="transaction-log-header-text">Plate</span>
                                <img
                                    src={sortConfig.key === 'plateNumber' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                    alt="Sort Arrow"
                                    className={`sort-arrow ${sortConfig.key === 'plateNumber' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                />
                            </div>
                        </th>
                        <th onClick={() => handleSort('duration')} className="sortable-column">
                            <div className="transaction-log-header-content">
                                <span className="transaction-log-header-text">Duration</span>
                                <img
                                    src={sortConfig.key === 'duration' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                    alt="Sort Arrow"
                                    className={`sort-arrow ${sortConfig.key === 'duration' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                />
                            </div>
                        </th>
                        <th onClick={() => handleSort('status')} className="sortable-column">
                            <div className="transaction-log-header-content">
                                <span className="transaction-log-header-text">Status</span>
                                <img
                                    src={sortConfig.key === 'status' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                    alt="Sort Arrow"
                                    className={`sort-arrow ${sortConfig.key === 'status' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                />
                            </div>
                        </th>
                        {!isTableNarrow && (
                            <>
                                <th onClick={() => handleSort('entryTime')} className="sortable-column">
                                    <div className="transaction-log-header-content">
                                        <span className="transaction-log-header-text">Date</span>
                                        <img
                                            src={sortConfig.key === 'entryTime' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                            alt="Sort Arrow"
                                            className={`sort-arrow ${sortConfig.key === 'entryTime' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                        />
                                    </div>
                                </th>
                                <th onClick={() => handleSort('totalAmount')} className="sortable-column">
                                    <div className="transaction-log-header-content">
                                        <span className="transaction-log-header-text">Cost</span>
                                        <img
                                            src={sortConfig.key === 'totalAmount' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                            alt="Sort Arrow"
                                            className={`sort-arrow ${sortConfig.key === 'totalAmount' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                        />
                                    </div>
                                </th>
                                <th onClick={() => handleSort('paymentStatus')} className="sortable-column">
                                    <div className="transaction-log-header-content">
                                        <span className="transaction-log-header-text">Payment</span>
                                        <img
                                            src={sortConfig.key === 'paymentStatus' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                                            alt="Sort Arrow"
                                            className={`sort-arrow ${sortConfig.key === 'paymentStatus' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                                        />
                                    </div>
                                </th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {filteredResults.map((session, index) => {
                        const sessionKey = getUniqueKey(session);
                        const isSelected = selectedEntries.has(sessionKey);

                        return (
                            <tr
                                key={session.sessionId}
                                onClick={() => handleRowClick(session)}
                                className="clickable-row"
                                style={{ backgroundColor: index % 2 === 0 ? '#31343C' : '#2B2E35' }}
                            >
                                <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                    <div className="custom-checkbox" style={{ cursor: 'pointer', margin: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleCheckboxChange(session)}
                                            className="hidden-checkbox"
                                            style={{ position: 'absolute', cursor: 'pointer' }}
                                        />
                                        <span
                                            className={`checkbox-styler ${isSelected ? 'checked' : ''}`}
                                            onClick={() => handleCheckboxChange(session)}
                                            style={{ cursor: 'pointer' }}
                                        ></span>
                                    </div>
                                </td>
                                <td>
                                    {session.plateNumber}
                                </td>
                                <td>{calculateDuration(session.entryTime, session.exitTime)}</td>
                                <td>
                                    {session.userType === 'Violator' && (
                                        <img src="/assets/violator.svg" alt="Violator" style={{ marginRight: 6, verticalAlign: 'middle', width: 13, height: 13 }} />
                                    )}
                                    <span className={`status-badge ${getSessionStatus(session).toLowerCase().replace(' ', '-')}`}>
                                        {getSessionStatus(session)}
                                    </span>
                                </td>
                                {!isTableNarrow && (
                                    <>
                                        <td>{new Date(session.entryTime).toISOString().split('T')[0]}</td>
                                        <td>{session.totalAmount !== null ? `$${session.totalAmount.toFixed(2)}` : 'TBD'}</td>
                                        <td>
                                            {getSessionStatus(session) !== 'Active' && getSessionStatus(session) !== 'Force End' && (
                                                <span className={`status-badge payment-status-${getPaymentStatusColor(session.paymentStatus)}`}>
                                                    {getPaymentStatusDisplayText(session.paymentStatus)}
                                                </span>
                                            )}
                                        </td>
                                    </>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {filteredResults.length === 0 ? (
                <div className="no-data-container">
                    <h3 className="no-results-header">No Results Found</h3>
                    <p>This lot has no Session Data available</p>
                </div>
            ) : (
                <>
                    {/* Only show loading indicator if hasMore is true and we have some entries already */}
                    {hasMore && !isLoading && sessions.length > 0 && (
                        <div
                            ref={loaderRef}
                            className="loading-indicator"
                        >
                            Scroll for more entries
                        </div>
                    )}

                    {/* Show loading state separately */}
                    {isLoading && (
                        <div className="loading-indicator">
                            Loading entries...
                        </div>
                    )}
                </>
            )}

            {showDetailModal && selectedSession && (
                <div className="modal-overlay" onClick={handleDetailModalClose}>
                    <div className="info-modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Session Details</h2>
                            <button className="download-session-button" onClick={() => handleDownloadSessionData(selectedSession)}>
                                <img src="/assets/DownloadArrow.svg" alt="Download" className="download-icon" />
                            </button>
                        </div>
                        <div className="detail-grid">
                            <div className="detail-column">
                                <div className="detail-item">
                                    <span className="detail-label">Plate Number:</span>
                                    <span className="detail-value">{selectedSession.plateNumber}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Entry Time:</span>
                                    <span className="detail-value">{new Date(selectedSession.entryTime).toLocaleString()}</span>
                                </div>
                                {selectedSession.exitTime && (
                                    <div className="detail-item">
                                        <span className="detail-label">Exit Time:</span>
                                        <span className="detail-value">{new Date(selectedSession.exitTime).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="detail-item">
                                    <span className="detail-label">Duration:</span>
                                    <span className="detail-value">{calculateDuration(selectedSession.entryTime, selectedSession.exitTime)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Status:</span>
                                    <span className="detail-value">
                                        <span className={`status-badge ${getSessionStatus(selectedSession).toLowerCase().replace(' ', '-')}`}>
                                            {getSessionStatus(selectedSession)}
                                        </span>
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Total Amount:</span>
                                    <span className="detail-value">{selectedSession.totalAmount !== null ? `$${selectedSession.totalAmount.toFixed(2)}` : 'TBD'}</span>
                                </div>
                                <div className="detail-item" style={{ position: 'relative' }}>
                                    <span className="detail-label">Payment Status:</span>
                                    <span className="detail-value">
                                        <span className={`status-badge payment-status-${getPaymentStatusColor(selectedSession.paymentStatus)}`}>
                                            {getPaymentStatusDisplayText(selectedSession.paymentStatus)}
                                        </span>
                                        <button
                                            className="edit-payment-status-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowPaymentStatusEdit(!showPaymentStatusEdit);
                                            }}
                                            title="Edit payment status"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                marginLeft: '8px',
                                                padding: '2px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                    </span>
                                    {showPaymentStatusEdit && (
                                        <div className="payment-status-edit-dropdown" style={{
                                            position: 'absolute',
                                            backgroundColor: '#2B2E35',
                                            border: '1px solid #444',
                                            borderRadius: '4px',
                                            padding: '8px',
                                            marginTop: '4px',
                                            zIndex: 1000,
                                            minWidth: '150px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                        }}>
                                            <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', fontWeight: 'bold' }}>
                                                Change Payment Status:
                                            </div>
                                            {[
                                                { value: 'NOT_REQUIRED', label: 'No Charge' },
                                                { value: 'PENDING', label: 'Pending' },
                                                { value: 'PAID_SINGLE', label: 'Paid' },
                                                { value: 'UNPAID', label: 'Unpaid' },
                                                { value: 'DISPUTED', label: 'Disputed' },
                                                { value: 'REFUNDED', label: 'Refunded' }
                                            ].map((option) => (
                                                <button
                                                    key={option.value}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePaymentStatusUpdate(option.value);
                                                    }}
                                                    disabled={isUpdatingPaymentStatus}
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#fff',
                                                        padding: '6px 8px',
                                                        cursor: isUpdatingPaymentStatus ? 'not-allowed' : 'pointer',
                                                        fontSize: '14px',
                                                        borderRadius: '2px',
                                                        opacity: isUpdatingPaymentStatus ? 0.6 : 1
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isUpdatingPaymentStatus) {
                                                            e.currentTarget.style.backgroundColor = '#444';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {selectedSession.ownerName && (
                                    <div className="detail-item">
                                        <span className="detail-label">OwnerID:</span>
                                        <span className="detail-value">{selectedSession.ownerName}</span>
                                    </div>
                                )}
                            </div>
                            <div className="detail-column">
                                {selectedSession.advancedPricingEnabled !== undefined && (
                                    <div className="detail-item">
                                        <span className="detail-label">Advanced Pricing Enabled:</span>
                                        <span className="detail-value">
                                            {selectedSession.advancedPricingEnabled ? 'Yes' : 'No'}
                                            <img
                                                src={selectedSession.advancedPricingEnabled ? "/assets/check.svg" : "/assets/x.svg"}
                                                alt={selectedSession.advancedPricingEnabled ? "Yes" : "No"}
                                                style={{ marginLeft: 6, verticalAlign: 'middle', width: 16, height: 16 }}
                                            />
                                        </span>
                                    </div>
                                )}
                                {selectedSession.vehicleRegistered !== undefined && (
                                    <div className="detail-item">
                                        <span className="detail-label">Vehicle Registered:</span>
                                        <span className="detail-value">
                                            {selectedSession.vehicleRegistered ? 'Yes' : 'No'}
                                            <img
                                                src={selectedSession.vehicleRegistered ? "/assets/check.svg" : "/assets/x.svg"}
                                                alt={selectedSession.vehicleRegistered ? "Yes" : "No"}
                                                style={{ marginLeft: 6, verticalAlign: 'middle', width: 16, height: 16 }}
                                            />
                                        </span>
                                    </div>
                                )}
                                {selectedSession.sessionType && (
                                    <div className="detail-item">
                                        <span className="detail-label">Session Type:</span>
                                        <span className="detail-value">{selectedSession.sessionType}</span>
                                    </div>
                                )}
                                {selectedSession.userType && (
                                    <div className="detail-item">
                                        <span className="detail-label">User Type:</span>
                                        <span className="detail-value">
                                            {selectedSession.userType}
                                            {selectedSession.userType === 'Violator' && (
                                                <img src="/assets/violator.svg" alt="Violator" style={{ marginLeft: 6, verticalAlign: 'middle', width: 13, height: 13 }} />
                                            )}
                                        </span>
                                    </div>
                                )}
                                {selectedSession.vehicleType && (
                                    <div className="detail-item">
                                        <span className="detail-label">Vehicle Type:</span>
                                        <span className="detail-value">{selectedSession.vehicleType}</span>
                                    </div>
                                )}
                                {showMoreDetails && (
                                    <>
                                        <div className="detail-item">
                                            <span className="detail-label">Session ID:</span>
                                            <span className="detail-value">{selectedSession.sessionId}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Lot ID:</span>
                                            <span className="detail-value">{selectedSession.lotId}</span>
                                        </div>
                                        {selectedSession.entryAlprId && (
                                            <div className="detail-item">
                                                <span className="detail-label">Entry ALPR ID:</span>
                                                <span className="detail-value">{selectedSession.entryAlprId}</span>
                                            </div>
                                        )}
                                        {selectedSession.exitAlprId && (
                                            <div className="detail-item">
                                                <span className="detail-label">Exit ALPR ID:</span>
                                                <span className="detail-value">{selectedSession.exitAlprId}</span>
                                            </div>
                                        )}
                                        {selectedSession.pricingId && (
                                            <div className="detail-item">
                                                <span className="detail-label">Pricing ID:</span>
                                                <span className="detail-value">{selectedSession.pricingId}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                            <button
                                onClick={() => setShowMoreDetails(!showMoreDetails)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ffffff',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                {showMoreDetails ? 'Show Less' : 'Show More'}
                            </button>
                        </div>
                        {getSessionStatus(selectedSession) === 'Active' && (
                            <div className="modal-actions">
                                <button
                                    className="force-end-button"
                                    onClick={handleForceEndSession}
                                    disabled={isForceEnding}
                                >
                                    {isForceEnding ? 'Force Ending...' : 'Force End Session'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Download File Modal */}
            <DownloadFileModal
                isOpen={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                onDownload={handleDownload}
                placeholderFilename={getDefaultFilename()}
            />

            {/* Connections Redirect Modal */}
            {showConnectionsModal && (
                <ConnectionsRedirectModal
                    onClose={handleCloseConnectionsModal}
                />
            )}

            {/* Info Modal */}
            {showInfoModal && (
                <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="info-modal-content info-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <button
                                className="close-button"
                                onClick={() => setShowInfoModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="info-content">
                            <div className="status-section">
                                <h3>Session Statuses</h3>
                                <div className="status-item">
                                    <span className="status-badge active">Active</span>
                                    <p>Session in progress</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge departed">Departed</span>
                                    <p>Session completed normally</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge force-end">Force End</span>
                                    <p>Session manually terminated</p>
                                </div>
                            </div>

                            <div className="status-section">
                                <h3>Payment Statuses</h3>
                                <div className="status-item">
                                    <span className="status-badge payment-status-green">No Charge</span>
                                    <p>No payment required</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-green">Paid</span>
                                    <p>Payment completed successfully</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-yellow">Pending</span>
                                    <p>Awaiting payment</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-yellow">Processing</span>
                                    <p>Payment being processed</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-yellow">Active Session</span>
                                    <p>Payment session started</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Unpaid</span>
                                    <p>Payment window expired</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Failed</span>
                                    <p>Payment attempt failed</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Expired</span>
                                    <p>Payment session expired</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Cancelled</span>
                                    <p>Payment cancelled by user</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Refunded</span>
                                    <p>Full amount refunded</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Refunded*</span>
                                    <p>Partial amount refunded</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Disputed</span>
                                    <p>Transaction disputed</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Chargeback</span>
                                    <p>Payment provider reversed</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Please Fix</span>
                                    <p>Requires manual intervention</p>
                                </div>
                                <div className="status-item">
                                    <span className="status-badge payment-status-red">Refunding</span>
                                    <p>Refund in progress</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionLog;
